import { t } from "i18next"
import React from "react"
import Recaptcha from "react-grecaptcha"
import { any, async, fromJSX, IOWidget, Updater } from "widgets-for-react"
import { FormControls } from "../../../../../../../Shared/Frontend/forms"
import { capitalize } from "../../../../../../../Shared/Frontend/utils/capitalize"
import { dataLayerServices } from "../../../../../seo/datalayer"
import { DonationFormState, DonationFormStepState, DonationFormType, donationFormUpdaters, getAmountsForForm, getManualAmount, getStep3Validation, isFieldRequired, isRecurringFormType, possibleSalutations, possibleSalutationsType, Step3PersonalData, } from "../donationFormBlock.state"
import { valutaDisplay } from "../utils"
import { title } from "process"
import { slugify } from "../../../../../../../Shared/Frontend/utils/slugify"
import { htmlDecode } from "../../../../utils/html"

type fieldUpdater<T> = (_: T) => void

type Updaters = {
  emailUpdater: fieldUpdater<string>,
  ibanUpdater: fieldUpdater<string>,
  salutationsUpdater: fieldUpdater<possibleSalutationsType>
  firstNameUpdater: fieldUpdater<string>,
  insertionUpdater: fieldUpdater<string>,
  lastNameUpdater: fieldUpdater<string>,
  postalCodeUpdater: fieldUpdater<string>,
  houseNumberUpdater: fieldUpdater<string>,
  houseNumberAdditionUpdater: fieldUpdater<string>,
  phoneUpdater: fieldUpdater<string>,
  acceptNewsLetterUpdater: fieldUpdater<boolean>,
  toggleMachtigingUpdater: fieldUpdater<boolean>,
  onBlur: (field?: keyof Step3PersonalData) => () => void,
  recaptchaUpdater: fieldUpdater<string>,
  onSubmit: (
    isPhoneFilled: boolean,
    formType: DonationFormType,
    amount: string,
    recaptcha: string,
    buttonText: string
  ) => void,
  goBack: () => void,
}

const Step3Layout = (props: {
  updaters: Updaters,
  state: DonationFormStepState<'3-PersonalForm'>,
}) => {
  const { updaters, state } = props
  const predefinedAmounts = getAmountsForForm(state)
  const manualAmount = getManualAmount(state)
  const customTitle = state.selectedFormType === "single-sign-on" || state.selectedFormType === "express"
    ? state.customTitle
    : undefined

  const salutationValidation = getStep3Validation(state, 'salutation')
  const firstNameValidation = getStep3Validation(state, 'firstName')
  const insertionValidation = getStep3Validation(state, 'insertion')
  const lastNameValidation = getStep3Validation(state, 'lastName')
  const postalCodeValidation = getStep3Validation(state, 'postalCode')
  const houseNumberValidation = getStep3Validation(state, 'houseNumber')
  const houseNumberAdditionValidation = getStep3Validation(state, 'houseNumberAddition')
  const phoneValidation = getStep3Validation(state, 'phone')
  const emailValidation = getStep3Validation(state, "email")
  const ibanValidation = getStep3Validation(state, 'iban')
  return <>
    <div className='donation__subheader'>
      <h2 id={slugify(htmlDecode(title))} className='donation__subtitle title--m'>
        {t(`DonationBlock:DonationForm.Title`)}
        {" "}
        {t(`DonationBlock:DonationForm.Label_${state.selectedFormType}`).toLowerCase()}
        {" "}
        {manualAmount ? valutaDisplay(manualAmount) : valutaDisplay(predefinedAmounts.amounts[predefinedAmounts.selectedIndex || 0].amount)}
        {" "}
        {customTitle}
      </h2>
      {Boolean(props.state.isPropositionDonation) === false &&
        <p className='donation__return text--s'>
          <button className="link link--default" onClick={_ => updaters.goBack()}>
            <span className="link__label">{t("DonationBlock:DonationForm.PersonaliaGoBack")}</span>
          </button>
        </p>
      }
    </div>

    <FormControls.FormRadioInGroup
      key={`donation-gender-b${state.blockIndex}`}
      name={`donation-gender-b${state.blockIndex}`}
      legend={t("DonationBlock:DonationForm.Label_salutation")}
      disabled={false}
      required={false}
      errorMessage={salutationValidation.kind == 'failed' ? salutationValidation.message : undefined}
      items={possibleSalutations.map((item, index) => ({
        controlId: `donation-gender--b${state.blockIndex}-${index}`,
        label: item.replace(/_/g, ' '),
        checked: item == state.input.salutation ? true : false,
        onChange: (checked) => checked ? updaters.salutationsUpdater(item) : () => { }
      }))}
    />

    <div className='donation__row row--30-30-40'>
      <FormControls.FormItemInGroup
        key={`donation-firstname-b${state.blockIndex}`}
        controlId={`donation-firstname-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_firstName")}
        disabled={false}
        required={isFieldRequired(state, 'firstName')}
        value={state.input.firstName}
        placeholder=''
        onChange={updaters.firstNameUpdater}
        onBlur={updaters.onBlur('firstName')}
        errorMessage={firstNameValidation.kind == 'failed' ? firstNameValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-infix-b${state.blockIndex}`}
        controlId={`donation-infix-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={<><span className="label--small">{t("DonationBlock:DonationForm.Label_insertion_s")}</span><span className="label--large">{t("DonationBlock:DonationForm.Label_insertion")}</span></>}
        disabled={false}
        required={isFieldRequired(state, 'insertion')}
        value={state.input.insertion}
        placeholder=''
        onChange={updaters.insertionUpdater}
        onBlur={updaters.onBlur('insertion')}
        errorMessage={insertionValidation.kind == 'failed' ? insertionValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-lastname-b${state.blockIndex}`}
        controlId={`donation-lastname-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_lastName")}
        disabled={false}
        required={isFieldRequired(state, 'lastName')}
        value={state.input.lastName}
        placeholder=''
        onChange={updaters.lastNameUpdater}
        onBlur={updaters.onBlur('lastName')}
        errorMessage={lastNameValidation.kind == 'failed' ? lastNameValidation.message : undefined}
      />
    </div>

    <div className='donation__row row--33'>
      <FormControls.FormItemInGroup
        key={`donation-postalcode-b${state.blockIndex}`}
        controlId={`donation-postalcode-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_postalCode")}
        disabled={false}
        required={isFieldRequired(state, 'postalCode')}
        value={state.input.postalCode}
        onBlur={updaters.onBlur('postalCode')}
        placeholder=''
        onChange={updaters.postalCodeUpdater}
        errorMessage={postalCodeValidation.kind == 'failed' ? postalCodeValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-housenumber-b${state.blockIndex}`}
        controlId={`donation-housenumber-b${state.blockIndex}`}
        type='text'
        inputMode='numeric'
        label={<><span className="label--small">{t("DonationBlock:DonationForm.Label_houseNumber_s")}</span><span className="label--large">{t("DonationBlock:DonationForm.Label_houseNumber")}</span></>}
        disabled={false}
        required={isFieldRequired(state, 'houseNumber')}
        value={state.input.houseNumber}
        placeholder=''
        onChange={updaters.houseNumberUpdater}
        onBlur={updaters.onBlur('houseNumber')}
        errorMessage={houseNumberValidation.kind == 'failed' ? houseNumberValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-addition-b${state.blockIndex}`}
        controlId={`donation-addition-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={<><span className="label--small">{t("DonationBlock:DonationForm.Label_houseNumberAddition_s")}</span><span className="label--large">{t("DonationBlock:DonationForm.Label_houseNumberAddition")}</span></>}
        disabled={false}
        required={isFieldRequired(state, 'houseNumberAddition')}
        value={state.input.houseNumberAddition}
        placeholder=''
        onChange={updaters.houseNumberAdditionUpdater}
        onBlur={updaters.onBlur('houseNumberAddition')}
        errorMessage={houseNumberAdditionValidation.kind == 'failed' ? houseNumberAdditionValidation.message : undefined}
      />
    </div>

    <div className='donation__row row--50-50'>
      <FormControls.FormItemInGroup
        key={`donation-street-b${state.blockIndex}`}
        controlId={`donation-street-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_street")}
        disabled={true}
        required={isFieldRequired(state, 'street')}
        value={state.input.street}
        placeholder=''
        onChange={() => { }}
        errorMessage={state.input.invalidHousenumberPostcodeCombination ? t("DonationBlock:DonationForm.Error_street") : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-city-b${state.blockIndex}`}
        controlId={`donation-city-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_city")}
        disabled={true}
        required={isFieldRequired(state, 'city')}
        value={state.input.city}
        placeholder=''
        onChange={() => { }}
        errorMessage={state.input.invalidHousenumberPostcodeCombination ? t("DonationBlock:DonationForm.Error_city") : undefined}
      />
    </div>

    <FormControls.FormItemInGroup
      key={`donation-tel-b${state.blockIndex}`}
      controlId={`donation-tel-b${state.blockIndex}`}
      type='tel'
      inputMode='tel'
      label={t("DonationBlock:DonationForm.Label_phone")}
      disabled={false}
      required={isFieldRequired(state, 'phone')}
      value={state.input.phone}
      placeholder=''
      onChange={updaters.phoneUpdater}
      onBlur={updaters.onBlur('phone')}
      errorMessage={phoneValidation.kind == 'failed' ? phoneValidation.message : undefined}
    />

    <FormControls.FormItemInGroup
      key={`donation-email-b${state.blockIndex}`}
      controlId={`donation-email-b${state.blockIndex}`}
      type='email'
      inputMode='email'
      label={t("DonationBlock:DonationForm.Label_email")}
      disabled={false}
      required={isFieldRequired(state, 'email')}
      value={state.input.email}
      placeholder=''
      onChange={updaters.emailUpdater}
      onBlur={updaters.onBlur('email')}
      errorMessage={state.validatorInput.email != undefined && state.validatorResult.path('email').kind == 'failed' && state.input.email == ''
        ? t("DonationBlock:DonationForm.Error_email_empty")
        : emailValidation.kind == 'failed' ? emailValidation.message : undefined}
    />

    <FormControls.FormCheckInGroup
      key={`donation-newsletter-b${state.blockIndex}`}
      name={`donation-newsletter-b${state.blockIndex}`}
      legend={undefined}
      required={true}
      items={[t("DonationBlock:DonationForm.Label_acceptNewsletter")].map((item, index) => ({
        controlId: `donation-newsletter-b${state.blockIndex}-${index}`,
        label: capitalize(item),
        checked: state.input.acceptNewsLetter,
        onChange: updaters.acceptNewsLetterUpdater
      }))}
    />
    <div id="recaptcha-content">
      <Recaptcha
        id="dtf-recaptcha"
        locale="NL_nl"
        sitekey={(document.getElementById('recaptchaToken') as any).value}
        callback={
          (r: string) => {
            updaters.recaptchaUpdater(r)
            const predefinedAmounts = getAmountsForForm(state)
            updaters.onSubmit(
              state.input.phone ? true : false,
              state.selectedFormType,
              manualAmount ? manualAmount.toString() : predefinedAmounts.amounts[predefinedAmounts.selectedIndex || 0].amount.toString(),
              r,
              state.submitButton
            )
          }
        }
        expiredCallback={() => window.grecaptcha.reset()}
        invisible
      />
    </div>

    {state.selectedFormType == 'single-sign-on' && state.optionForMachtiging && <>
      <FormControls.FormRadioInGroup
        key={`donation-machtiging-b${state.blockIndex}`}
        name={`donation-machtiging-b${state.blockIndex}`}
        legend={t("DonationBlock:DonationForm.Label_MachtigingSelector")}
        disabled={false}
        required={false}
        style='button'
        items={['iDeal', 'Machtiging'].map((item, index) => ({
          controlId: `donation-machtiging-b${state.blockIndex}-${index}`,
          label: t(`DonationBlock:DonationForm.Label_${item}`),
          checked: (
            item == 'Machtiging' && state.input.machtigingSelected
            || item == 'iDeal' && !state.input.machtigingSelected),
          onChange: _ => updaters.toggleMachtigingUpdater(item == "Machtiging")
        })) || []}
      />
    </>}

    {isRecurringFormType(state) && <>
      <h3 className="donation__seperator title--xs">{t("DonationBlock:DonationForm.payment_header")}</h3>
      <FormControls.FormItemInGroup
        key={`donation-iban-b${state.blockIndex}`}
        controlId={`donation-iban-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_iban")}
        disabled={false}
        required={isFieldRequired(state, 'iban')}
        value={state.input.iban}
        placeholder=''
        onChange={updaters.ibanUpdater}
        onBlur={updaters.onBlur('iban')}
        errorMessage={ibanValidation.kind ==  "failed" ? ibanValidation.message : undefined}
      />

      <div className='fade fs__alert text--styled text--s fs__alert--info  alert alert-info show'>
        <div dangerouslySetInnerHTML={{ __html: t("DonationBlock:DonationForm.authorization_disclaimer") }} />
      </div>
    </>}

    <div className='donation__action'>
      <button
        className='button button--primary'
        onClick={() => {
          window.grecaptcha.reset()
          window.grecaptcha.execute()
        }}
        disabled={state.submitState.kind == 'loading'}
      >
        <span className="button__label">{state.submitButton}</span>
      </button>
    </div>

    <div className='donation__avg text--styled text--s'>
      <p dangerouslySetInnerHTML={{ __html: t("DonationBlock:DonationForm.avg") }} />
    </div>
  </>
}

export const step3PersonalFormWidget: IOWidget<DonationFormStepState<'3-PersonalForm'>, Updater<DonationFormState>> = s =>
  any([
    s.validator.widget().map((v) => donationFormUpdaters.step3validationUpdater((_) => v)),
    async(s.zipCodeLoader).map(donationFormUpdaters.step3ZipCodeLoaderUpdater),
    fromJSX(updater => <Step3Layout
      updaters={{
        recaptchaUpdater: (str: string) => {
          updater(donationFormUpdaters.recaptchaUpdater(str))
        },
        emailUpdater: (str: string) => {
          !s.isPropositionDonation && dataLayerServices.newDataLayerEventInteraction(),
            updater(donationFormUpdaters.inputStep3Updater('email', str))
        },
        ibanUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('iban', str))
        },
        salutationsUpdater: (sal: possibleSalutationsType) => {
          updater(donationFormUpdaters.inputStep3Updater('salutation', sal))
        },
        firstNameUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('firstName', str))
        },
        insertionUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('insertion', str))
        },
        lastNameUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('lastName', str))
        },
        postalCodeUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('postalCode', str))
        },
        houseNumberUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('houseNumber', str))
        },
        houseNumberAdditionUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('houseNumberAddition', str))
        },
        phoneUpdater: (str: string) => {
          updater(donationFormUpdaters.inputStep3Updater('phone', str))
        },
        acceptNewsLetterUpdater: (bool: boolean) => {
          updater(donationFormUpdaters.inputStep3Updater('acceptNewsLetter', bool))
        },
        toggleMachtigingUpdater: (bool: boolean) => {
          updater(donationFormUpdaters.inputStep3Updater("machtigingSelected", bool))
        },
        onBlur: (field?: keyof Step3PersonalData) => () => {
          updater(donationFormUpdaters.step3ValidateFields(field))
        },
        onSubmit: (isPhoneFilled: boolean, formType: DonationFormType, amount: string, recaptcha: string, buttonText: string) => {
          isPhoneFilled ? dataLayerServices.telemarketingDataLayer("Donation") : {},
          dataLayerServices.donationCheckoutDataLayer(
            3,
            formType,
            formType == 'monthly-sign-on' ? 'monthly' : formType == 'single-sign-on' ? 'once' : 'yearly',
            [{
              name: 'donation',
              price: amount,
              variant: 'fo-3',
              category: 'giftgever',
              quantity: '1'
            }])
          dataLayerServices.scheduleTransactionCheckoutDataLayer(
            amount,
            window.location.href,
            formType == 'monthly-sign-on' ? 'monthly' : formType == 'single-sign-on' ? 'once' : 'yearly',
            [{
              name: 'donation',
              price: amount,
              variant: formType == 'monthly-sign-on' ? 'fo-3' : 'fo-2',
              category: 'giftgever',
              quantity: '1'
            }])
          dataLayerServices.scheduleSuccessDataLayer(window.location.href)
          dataLayerServices.newDataLayerEventButton("2", buttonText)
          updater(donationFormUpdaters.transitionFromStep3(recaptcha))
        },
        goBack: () => updater(donationFormUpdaters.transitionFromStep3ToStep2())
      }}
      state={s}
    />
    )
  ])
